import {Icon, Show} from 'platform/foundation';
import styled, {css} from 'styled-components';

import {ReactNode} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {Action} from '../Actions/Actions';
import {TabIconVariant} from './TabIconVariant';
import {getIconPropsFromVariant} from './utils/getIconPropsFromVariant';

export type TabVariants = 'container' | 'condensed' | 'fullHeight' | 'default';

export type TabProps = TestIdProps & {
  id: string;
  title: string;
  content: ReactNode;
  isDisabled?: boolean;
  icon?: TabIconVariant;
  actions?: Action[];
  onClick?: (id: string) => void;
  onFocus?: (id: string) => void;
  isSelected?: boolean;
  isFocused?: boolean;
  variant?: TabVariants;
  isFullWidth?: boolean;
  tabId?: string;
  panelId?: string;
  tabIndex?: number;
  ariaLabel?: string;
  ariaDescription?: string;
};

/**
 * @internal
 */
export function Tab(props: Omit<TabProps, 'content' | 'actions'>) {
  const tabIcon = getIconPropsFromVariant(props.icon);

  const handleClick = () => {
    if (props.isDisabled) {
      return;
    }
    props.onClick?.(props.id);
  };

  const handleFocus = () => {
    if (props.isDisabled) {
      return;
    }
    // Only update focus state, don't activate the tab
    props.onFocus?.(props.id);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    // Space and Enter should activate the tab
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      handleClick();
    }
  };

  // Generate accessible label combining title with optional aria label
  const accessibleLabel = props.ariaLabel || `${props.title}`;

  // Create description text for screen readers
  const descriptionText =
    props.ariaDescription ||
    `${props.isSelected ? 'Currently selected' : 'Not selected'} tab${props.isDisabled ? ', disabled' : ''}`;

  return (
    <StyledTab
      id={props.tabId}
      role="tab"
      onClick={handleClick}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      $isDisabled={props.isDisabled}
      $variant={props.variant}
      $isSelected={props.isSelected}
      $isFocused={props.isFocused}
      $isFullWidth={props.isFullWidth}
      data-testid={suffixTestId('tab', props)}
      data-tab-id={props.id}
      aria-label={accessibleLabel}
      aria-describedby={props.ariaDescription ? `${props.tabId}-desc` : undefined}
      aria-selected={props.isSelected}
      aria-disabled={props.isDisabled}
      aria-controls={props.panelId}
      aria-current={props.isSelected ? 'page' : undefined}
      disabled={props.isDisabled}
      tabIndex={props.tabIndex ?? (props.isSelected ? 0 : -1)}
      type="button"
      title={props.isDisabled ? `${props.title} (disabled)` : props.title}
    >
      <Show when={isNotNil(tabIcon)}>
        <StyledIconWrapper>
          <Icon
            value={tabIcon?.key}
            color={tabIcon?.color}
            data-testid={suffixTestId('tabIcon', props)}
            aria-hidden="true"
          />
        </StyledIconWrapper>
      </Show>
      {props.title}
      {props.ariaDescription && (
        <StyledScreenReaderOnly id={`${props.tabId}-desc`}>
          {descriptionText}
        </StyledScreenReaderOnly>
      )}
    </StyledTab>
  );
}

const getVariantStyles = ($variant?: TabProps['variant']) => {
  switch ($variant) {
    case 'container':
      return css<StyledTabProps>`
        padding-top: 14px;
        padding-bottom: 16px;
        padding-inline-start: 16px;
        padding-inline-end: 16px;
        border-style: solid;
        background-color: ${({theme, $isSelected}) =>
          $isSelected
            ? theme.colors.palettes.white[10][100]
            : theme.colors.palettes.neutral[10][100]};
        border-top-width: 2px;
        border-top-color: ${({theme, $isSelected}) =>
          $isSelected ? theme.colors.palettes.blue[60][100] : 'transparent'};
        color: ${({theme}) => theme.colors.text.primary};

        &:hover {
          background-color: ${({theme}) => theme.colors.palettes.neutral[20][100]};
        }

        &:focus-visible {
          outline: 2px solid ${({theme}) => theme.colors.general.accent};
          outline-offset: 2px;
          border: 0px;
          background-color: ${({theme, $isFocused}) =>
            $isFocused ? theme.colors.palettes.neutral[20][100] : undefined};
        }

        &:focus:not(:focus-visible) {
          outline: none;
        }

        &:disabled {
          background-color: ${({theme}) => theme.colors.palettes.neutral[10][100]};
          border-color: ${({theme}) => theme.colors.palettes.neutral[10][100]};
        }
      `;
    case 'condensed':
    case 'fullHeight':
      return css<StyledTabProps>`
        padding-top: 0;
        padding-bottom: 6px;
        border-style: solid;
        border-bottom-width: ${({$isDisabled}) => ($isDisabled ? 0 : '2px')};
        border-color: ${({theme, $isSelected, $isFocused}) => {
          if ($isSelected) {
            return theme.colors.general.accent;
          }
          if ($isFocused) {
            return theme.colors.palettes.neutral[50][100];
          }
          return 'transparent';
        }};
        border-bottom-style: solid;
        cursor: ${({$isDisabled}) => ($isDisabled ? 'not-allowed' : 'pointer')};
        &:hover {
          border-color: ${({theme, $isSelected}) =>
            $isSelected ? theme.colors.general.accent : theme.colors.palettes.neutral[50][100]};

          &:active {
            border-bottom: ${({theme, $isDisabled}) =>
              !$isDisabled ? `2px solid ${theme.colors.palettes.neutral[100][100]}` : 'unset'};
          }
        }

        &:focus-visible {
          outline: 2px solid
            ${({theme, $isSelected}) =>
              $isSelected ? theme.colors.general.accent : theme.colors.palettes.neutral[50][100]};
          outline-offset: 2px;
          border: 0px;
        }

        &:focus:not(:focus-visible) {
          outline: none;
        }
      `;
    default:
      return css<StyledTabProps>`
        border-style: solid;
        padding-top: 16px;
        padding-bottom: 14px;
        border-bottom-width: 2px;
        border-color: ${({theme, $isSelected, $isFocused}) => {
          if ($isSelected) {
            return theme.colors.general.accent;
          }
          if ($isFocused) {
            return theme.colors.palettes.neutral[50][100];
          }
          return 'transparent';
        }};
        &:hover {
          border-bottom-color: ${({theme, $isSelected}) =>
            $isSelected ? theme.colors.general.accent : theme.colors.palettes.neutral[50][100]};

          &:active {
            border-bottom-color: ${({theme, $isDisabled}) =>
              `${theme.colors.palettes.neutral[$isDisabled ? 50 : 100][100]}`};
          }
        }

        &:focus-visible {
          outline: 2px solid
            ${({theme, $isSelected}) =>
              $isSelected ? theme.colors.general.accent : theme.colors.palettes.neutral[50][100]};
          outline-offset: 2px;
          border: 0px;
        }

        &:focus:not(:focus-visible) {
          outline: none;
        }
      `;
  }
};

type StyledTabProps = {
  $isSelected?: TabProps['isSelected'];
  $isFocused?: TabProps['isFocused'];
  $variant?: TabProps['variant'];
  $isDisabled?: TabProps['isDisabled'];
  $isFullWidth?: TabProps['isFullWidth'];
};

const StyledTab = styled.button<StyledTabProps>`
  outline: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  color: ${({theme, $isSelected, $isFocused}) => {
    if ($isSelected) {
      return theme.colors.general.accent;
    }
    if ($isFocused) {
      return theme.colors.palettes.neutral[70][100];
    }
    return theme.colors.text.primary;
  }};
  font-size: ${({theme}) => theme.fontSizes.text.base};
  line-height: ${({theme}) => theme.lineHeights.text.base};
  font-weight: ${({theme}) => theme.fontWeights.medium};
  padding-inline-start: 0;
  padding-inline-end: 0;
  padding-top: 0;
  padding-bottom: 0;
  cursor: pointer;
  user-select: none;
  pointer-events: ${({$isDisabled}) => ($isDisabled ? 'none' : 'auto')};
  flex: ${({$isFullWidth}) => ($isFullWidth ? '100%' : 'none')};
  border-style: solid;

  &:disabled {
    color: ${({theme}) => theme.colors.palettes.neutral[900][40]};
    border-color: transparent;
    cursor: not-allowed;

    &:active {
      border-color: transparent;
    }
  }

  ${({$variant}) => getVariantStyles($variant)}
`;

const StyledIconWrapper = styled.span`
  margin-right: 12px;
`;

const StyledScreenReaderOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
